/**
 * N2ScorecardModelPage — per-model deep dive.
 *
 * Routes: /n2/scorecard/cot | /gex | /iv | /avwap
 *
 * Structure per model:
 *   - Sub-nav
 *   - Header (model title + how-to-read + how-to-trade tagline)
 *   - Methodology sentence (what the model measures, what the outcome tests)
 *   - Per-symbol aggregate tiles
 *   - Model-only reading log (log with model filter pinned)
 */

import React, { useMemo } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { N2Frame } from './N2Frame';
import { palette } from './theme';
import { Eyebrow, PageTitle, PageLede } from './N2Primitives';
import { unifyRows, ScorecardPayload, ScorecardModel } from './notchesScorecardService';
import {
  ReadingLog, AggregateGrid,
  LoadingPanel, ErrorPanel, ScorecardSubNav, detectConfluence,
} from './notchesScorecardShared';
import { useNotchesScorecard } from './useNotchesScorecard';

interface ModelDef {
  slug: string;
  model: ScorecardModel;
  title: string;
  headline: string;
  lede: string;
  block: keyof Pick<ScorecardPayload, 'cot' | 'gex' | 'iv' | 'avwap'>;
  methodology: (data: ScorecardPayload) => string;
}

const MODEL_DEFS: ModelDef[] = [
  {
    slug: 'cot',
    model: 'COT',
    title: 'COT notches',
    headline: 'Leveraged-money positioning · 4-week directional',
    lede:
      'The CFTC publishes leveraged-money (hedge-fund) positioning every Tuesday for each futures contract. When the z-score of net positioning is stretched or extreme, the model fires a directional notch: SHORT expects the underlying to fall, LONG expects it to rise.',
    block: 'cot',
    methodology: (d) =>
      `Hit rule: the tradable ETF proxy moves in the implied direction over the next ${d.cot.meta.horizonDays} calendar days. SHORT hits on a drop, LONG hits on a rise. Neutral tags are not published.`,
  },
  {
    slug: 'gex',
    model: 'GEX',
    title: 'GEX notches',
    headline: 'Dealer gamma regime · 5-day realized-vol horizon',
    lede:
      'Dealer positioning in the options chain sets a volatility regime. Short-gamma regimes amplify moves (dealers trade with the market); long-gamma regimes dampen them (dealers trade against). The notch fires when the chain-derived regime tag changes at the end of a trading day.',
    block: 'gex',
    methodology: (d) =>
      `Hit rule: NEG_GEX fires expect vol expansion — hit when 5-day mean true-range is ≥ ${d.gex.meta.expandMult}× the prior 20-day baseline. POS_GEX fires expect compression — hit when 5-day TR is ≤ ${d.gex.meta.compressMult}× baseline. NEAR_GAMMA_FLIP tags are transitions and are not published.`,
  },
  {
    slug: 'iv',
    model: 'IV',
    title: 'IV notches',
    headline: 'IV rank crossings · 10-day mean-reversion horizon',
    lede:
      'Implied volatility rank measures where current IV sits vs the symbol\'s own IV history. When IVR crosses INTO an extreme band (≥70 high or ≤30 low), the model fires a mean-reversion notch. High implies IV should fall; low implies IV should rise.',
    block: 'iv',
    methodology: (d) =>
      `Trigger: IVR crosses into ≥${d.iv.meta.highBand} (high) or ≤${d.iv.meta.lowBand} (low) from mid. Stays quiet while inside the band. Hit rule: IVR reverts past ${d.iv.meta.revertLevel} within ${d.iv.meta.windowDays} trading days.`,
  },
  {
    slug: 'avwap',
    model: 'AVWAP',
    title: 'AVWAP notches',
    headline: 'Anchored VWAP support tests · 5-day defense outcome',
    lede:
      'AVWAP anchored to the trailing swing low is the average price paid by every participant since that low — a level institutions typically defend. The notch fires when spot returns to test the line after having been well above it.',
    block: 'avwap',
    methodology: (d) =>
      `Anchor: rolling ${d.avwap.meta.anchorLookback}-day swing low (recomputed daily). Trigger: today's daily low touches within 0.5% of AVWAP after prior ${d.avwap.meta.priorWindow} sessions averaged >${d.avwap.meta.priorAwayPct}% above. Hit rule (${d.avwap.meta.forwardDays} trading days later): close above AVWAP = hit; close ${d.avwap.meta.breakPct}%+ below = miss; anything between = neutral.`,
  },
];

export const N2ScorecardModelPage: React.FC = () => {
  const { model: slug } = useParams<{ model: string }>();
  const def = MODEL_DEFS.find((m) => m.slug === (slug || '').toLowerCase());
  const { data, err } = useNotchesScorecard();

  const confluenceEvents = useMemo(() => {
    if (!data) return [];
    const unified = unifyRows(data.cot.rows, data.gex.rows, data.iv.rows, data.avwap.rows);
    return detectConfluence(unified);
  }, [data]);

  const modelRows = useMemo(() => {
    if (!data || !def) return [];
    return unifyRows(data.cot.rows, data.gex.rows, data.iv.rows, data.avwap.rows)
      .filter((r) => r.model === def.model);
  }, [data, def]);

  if (!def) return <Navigate to="/n2/scorecard" replace />;

  return (
    <N2Frame page={`scorecard/${def.slug}`}>
      <div style={{ maxWidth: 1100, margin: '0 auto', padding: '48px 32px 40px' }}>
        <ScorecardSubNav />

        <div style={{ paddingTop: 24 }}>
          <Eyebrow>Scorecard · {def.title}</Eyebrow>
          <PageTitle italic="">
            {def.title}
          </PageTitle>
          <div style={{
            fontFamily: 'var(--sans)', fontSize: 12, fontWeight: 700,
            letterSpacing: '0.14em', textTransform: 'uppercase',
            color: palette.goldDeep, marginTop: 8, marginBottom: 20,
          }}>{def.headline}</div>
          <PageLede>{def.lede}</PageLede>
        </div>

        {err && <ErrorPanel message={err} />}
        {!data && !err && <LoadingPanel />}
        {data && <>
          <div style={{ marginTop: 40 }}>
            <AggregateGrid aggregates={data[def.block].aggregates} />
          </div>

          <div style={{
            marginTop: 24, padding: '16px 20px',
            border: `1px solid ${palette.line}`,
            background: palette.paper2, borderRadius: 3,
          }}>
            <div style={{
              fontFamily: 'var(--sans)', fontSize: 10, fontWeight: 700,
              letterSpacing: '0.22em', textTransform: 'uppercase',
              color: palette.ink3, marginBottom: 6,
            }}>Methodology</div>
            <div style={{
              fontFamily: 'var(--sans)', fontSize: 13, color: palette.ink2, lineHeight: 1.55,
            }}>{def.methodology(data)}</div>
          </div>

          <ReadingLog
            rows={modelRows}
            confluenceEvents={confluenceEvents}
            pinnedModel={def.model}
          />
        </>}
      </div>
    </N2Frame>
  );
};

export default N2ScorecardModelPage;
